import React, { useState, useEffect, useRef } from 'react'
import { Typography, withStyles, Popover } from '@material-ui/core'
import { CoreTextField } from 'src/components/core'
import { useSelector, useDispatch } from 'react-redux'
import URL from 'src/api/url/api'
import styles from './styles'
import { mediaImageUploadAction } from 'src/reducers/media-image/action'

import FontPicker from 'font-picker-react'
import { ChromePicker } from 'react-color'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import ContainerMediaGalery from 'src/container/image-gallery'
import { track } from '@amplitude/analytics-browser'
import { PatchContent, PatchData } from 'src/model/patch'
import { CoreIconButton } from 'src/components/core'
import { Icon } from '@iconify/react'
// Helper function to get the correct image URL
const getImageUrl = url => {
  if (!url) return ''
  // Check if the URL already contains https, don't add URL.MEDIA in that case
  return url.includes('https') ? url : `${URL.MEDIA}${url}`
}

const GeneralSettings = props => {
  const { classes, onSave, initialSettings } = props
  const dispatch = useDispatch()
  const entity = useSelector(state => state.course.course)

  const [initialized, setInitialized] = useState(false)
  const [fontsLoaded, setFontsLoaded] = useState(false)

  // Create a local state object to track image and color settings
  const [localSettings, setLocalSettings] = useState({
    primaryColor: '#015ffb',
    favicon: null,
    logo: null,
    thumbnail: null,
  })

  // UI state variables
  const [primaryColor, setPrimaryColor] = useState('#015ffb')
  const [favicon, setFavicon] = useState(null)
  const [logo, setLogo] = useState(null)
  const [thumbnail, setThumbnail] = useState(null)

  // Keep font handling like the original implementation

  const pageUrl = window.location.pathname.split('/')[1]
  const courseSettings = useSelector(state => state.course.landingPageSettings)
  const collectionSettings = useSelector(
    state => state.collection.landingPageSettings,
  )
  const landingPageData =
    pageUrl === 'mini-course' ? courseSettings || {} : collectionSettings || {}
  const [font, setFont] = useState(landingPageData?.font || 'Inter')

  useEffect(() => {
    if (landingPageData) {
      // Initialize UI state
      const initialColor = landingPageData.primaryColor || '#015ffb'
      const initialFavicon = landingPageData.favicon || null
      const initialLogo = landingPageData.logo || null
      const initialThumbnail = landingPageData.thumbnail || null

      // Update UI state
      setPrimaryColor(initialColor)
      setFavicon(initialFavicon)
      setLogo(initialLogo)
      setThumbnail(initialThumbnail)
      setFont(landingPageData.font || 'Inter')

      // Update local settings object (excluding font)
      setLocalSettings({
        primaryColor: initialColor,
        favicon: initialFavicon,
        logo: initialLogo,
        thumbnail: initialThumbnail,
        // Preserve any other settings that might exist
        ...landingPageData,
      })

      setInitialized(true)
    }
  }, [landingPageData])

  // Update settings - keeps track of image changes while handling font separately
  const updateSetting = (key, value) => {
    // Handle font the original way
    if (key === 'font') {
      // Just call onSave directly with the updated font
      onSave({
        ...landingPageData,
        font: value,
      })
      return
    }

    // For other settings (images, color), update the local settings
    setLocalSettings(prevSettings => {
      const updatedSettings = {
        ...prevSettings,
        [key]: value,
      }

      // Then call the parent onSave with all updated settings
      onSave(updatedSettings)

      return updatedSettings
    })
  }

  const handlePrimaryColorChange = color => {
    const newValue = color.hex
    setPrimaryColor(newValue)
    updateSetting('primaryColor', newValue)
  }

  const handleFontChange = nextFont => {
    const newFontFamily = nextFont.family

    // Keep the original font handling logic
    setFont(newFontFamily)

    // Call updateSetting unconditionally to ensure it gets updated
    // even if the font appears to be the same (this fixes the font issue)
    updateSetting('font', newFontFamily)
  }

  // Open media gallery for logo
  const handleLogoGalleryOpen = () => {
    track('logo_media_gallery_clicked')

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: `
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
              <Icon icon="ic:round-add-a-photo" color="#00AB55" width="24" height="24" />
              <Typography style="margin-left: 8; color: #002E47; fontWeight: 600; fontSize: 16;">
                Media Gallery - Logo
              </Typography>
            </div>
          `,
          component: (
            <ContainerMediaGalery
              onOk={items => {
                setLogo(items[0].url)
                updateSetting('logo', items[0].url)
                dispatch(messageBoxHideAction('message-box-1'))
              }}
              setImageURL={() => {}}
            />
          ),
          draggable: true,
          maxHeight: '70vh',
        }),
      ),
    )
  }

  // Open media gallery for favicon
  const handleFaviconGalleryOpen = () => {
    track('favicon_media_gallery_clicked')

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: `
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
              <Icon icon="ic:round-add-a-photo" color="#00AB55" width="24" height="24" />
              <Typography style="margin-left: 8; color: #002E47; fontWeight: 600; fontSize: 16;">
                Media Gallery - Favicon
              </Typography>
            </div>
          `,
          component: (
            <ContainerMediaGalery
              onOk={items => {
                setFavicon(items[0].url)
                updateSetting('favicon', items[0].url)
                dispatch(messageBoxHideAction('message-box-1'))
              }}
              setImageURL={() => {}}
            />
          ),
          draggable: true,
          maxHeight: '70vh',
        }),
      ),
    )
  }

  // Open media gallery for thumbnail
  const handleThumbnailGalleryOpen = () => {
    track('thumbnail_media_gallery_clicked')

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: `
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
              <Icon icon="ic:round-add-a-photo" color="#00AB55" width="24" height="24" />
              <Typography style="margin-left: 8; color: #002E47; fontWeight: 600; fontSize: 16;">
                Media Gallery - Thumbnail
              </Typography>
            </div>
          `,
          component: (
            <ContainerMediaGalery
              onOk={items => {
                setThumbnail(items[0].url)
                updateSetting('thumbnail', items[0].url)
                dispatch(messageBoxHideAction('message-box-1'))
              }}
              setImageURL={() => {}}
            />
          ),
          draggable: true,
          maxHeight: '70vh',
        }),
      ),
    )
  }

  const handlePrimaryColorClick = event => {
    setPrimaryColorAnchorEl(event.currentTarget)
  }

  const handlePrimaryColorClose = () => {
    setPrimaryColorAnchorEl(null)
  }

  const [primaryColorAnchorEl, setPrimaryColorAnchorEl] = useState(null)

  const handleFontsLoaded = () => {
    setFontsLoaded(true)
  }
  const fontPickerRef = useRef()

  return (
    <div className={classes.container}>
      <Typography
        fontSize="16px"
        fontWeight={400}
        fontFamily="Inter"
        className={classes.header}
      >
        General settings
      </Typography>
      <div className={classes.headerContainer}>
        <div className={classes.section}>
          <div>
            <Typography fontSize="14px" fontWeight={500} fontFamily="Inter">
              Typography
            </Typography>
            <Typography fontSize="12px" fontWeight={400} color="#919EAB">
              Customize the heading and paragraph fonts on your landing page.
            </Typography>
          </div>

          <FontPicker
            ref={fontPickerRef}
            apiKey="AIzaSyCrUWg_5VMW7DzqXAmN8iuB503bLwYfuxQ"
            limit={500}
            label="Typography"
            key="fontPicker"
            styles={{
              width: '90%',
            }}
            activeFontFamily={font}
            onChange={nextFont => {
              const newFontFamily = nextFont.family
              setFont(newFontFamily)
              updateSetting('font', newFontFamily)
            }}
          />
        </div>

        {pageUrl !== 'collection' && (
          <div className={classes.section}>
            <div>
              <Typography fontSize="14px" fontWeight={500} fontFamily="Inter">
                Primary color
              </Typography>
              <Typography fontSize="12px" fontWeight={400} color="#919EAB">
                Set a custom primary color for your landing page.
              </Typography>
            </div>
            <div className={classes.colorPickerContainer}>
              <div
                className={classes.pickerBg}
                onClick={handlePrimaryColorClick}
              >
                <span
                  className={classes.pickerSpan}
                  style={{ backgroundColor: primaryColor }}
                />
              </div>
              <Typography fontSize="12px" fontWeight={400}>
                {primaryColor}
              </Typography>
              <Popover
                open={Boolean(primaryColorAnchorEl)}
                anchorEl={primaryColorAnchorEl}
                onClose={handlePrimaryColorClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <ChromePicker
                  color={primaryColor}
                  onChange={handlePrimaryColorChange}
                />
              </Popover>
            </div>
          </div>
        )}

        {/* Logo section with Media Gallery */}
        <div className={classes.sectionLogo}>
          <div>
            <Typography fontSize="14px" fontWeight={500} fontFamily="Inter">
              Page logo
            </Typography>
            <Typography fontSize="12px" fontWeight={400} color="#919EAB">
              This logo image will appear in the landing page title area.
            </Typography>
          </div>
          <div className={classes.uploadContainer}>
            {logo ? (
              <div className={classes.imagePreview}>
                <div
                  className={classes.logoView}
                  style={{
                    background: `url(${getImageUrl(logo)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className={classes.iconButton}>
                    <CoreIconButton
                      color="white"
                      size="small"
                      onClick={() => {
                        setLogo(null)
                        updateSetting('logo', null)
                      }}
                    >
                      <Icon icon="bx:trash-alt" />
                    </CoreIconButton>
                  </div>
                </div>
                <Typography fontSize="12px" fontWeight={400} color="#919EAB">
                  Supported formats: JPG, GIF, or PNG. Maximum file size: 500KB
                  <br />
                  Recommended aspect ratio: 1:1
                </Typography>
              </div>
            ) : (
              <div className={classes.uploadBoxLogoContainer}>
                <div
                  className={classes.uploadBoxLogo}
                  onClick={handleLogoGalleryOpen}
                >
                  <div className={classes.plusIcon}>+</div>
                </div>
                <Typography fontSize="12px" fontWeight={400} color="#919EAB">
                  Supported formats: JPG, GIF, or PNG. Maximum file size: 500KB
                  <br />
                  Recommended aspect ratio: 1:1
                </Typography>
              </div>
            )}
          </div>
        </div>
        <div className={classes.section}>
          <div>
            <Typography fontSize="14px" fontWeight={500} fontFamily="Inter">
              Favicon
            </Typography>
            <Typography fontSize="12px" fontWeight={400} color="#919EAB">
              Favicons help users recognize your brand in browser tabs.
            </Typography>
          </div>
          <div className={classes.uploadContainer}>
            {favicon ? (
              <div className={classes.imagePreview}>
                <div
                  className={classes.faviconView}
                  style={{
                    background: `url(${getImageUrl(favicon)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className={classes.iconButton}>
                    <CoreIconButton
                      color="white"
                      size="small"
                      onClick={() => {
                        setFavicon(null)
                        updateSetting('favicon', null)
                      }}
                    >
                      <Icon icon="bx:trash-alt" />
                    </CoreIconButton>
                  </div>
                </div>
                <Typography fontSize="12px" fontWeight={400} color="#919EAB">
                  Supported formats: PNG or ICO. Recommended size: 64×64px
                </Typography>
              </div>
            ) : (
              <div className={classes.uploadBoxContainer}>
                <div
                  className={classes.uploadBoxFavicon}
                  onClick={handleFaviconGalleryOpen}
                >
                  <div className={classes.plusIcon}>+</div>
                </div>
                <Typography fontSize="12px" fontWeight={400} color="#919EAB">
                  Supported formats: PNG or ICO. Recommended size: 64×64px
                </Typography>
              </div>
            )}
          </div>
        </div>

        {/* Thumbnail section with Media Gallery */}
        <div className={classes.section}>
          <div>
            <Typography fontSize="14px" fontWeight={500} fontFamily="Inter">
              Thumbnail image
            </Typography>
            <Typography fontSize="12px" fontWeight={400} color="#919EAB">
              Shown in mini course cards and social posts.
            </Typography>
          </div>
          <div className={classes.uploadContainer}>
            {thumbnail ? (
              <div className={classes.imagePreview}>
                <div
                  className={classes.thumbnailView}
                  style={{
                    background: `url(${getImageUrl(thumbnail)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className={classes.iconButton}>
                    <CoreIconButton
                      color="white"
                      size="small"
                      onClick={() => {
                        setThumbnail(null)
                        updateSetting('thumbnail', null)
                      }}
                    >
                      <Icon icon="bx:trash-alt" />
                    </CoreIconButton>
                  </div>
                </div>
                <Typography fontSize="12px" fontWeight={400} color="#919EAB">
                  Supported formats: JPG, or PNG. Maximum file size: 500KB
                  <br />
                  Recommended min. size: 284 × 160 px
                </Typography>
              </div>
            ) : (
              <div className={classes.uploadBoxContainer}>
                <div
                  className={classes.uploadBox}
                  onClick={handleThumbnailGalleryOpen}
                >
                  <div className={classes.plusIcon}>+</div>
                </div>
                <Typography fontSize="12px" fontWeight={400} color="#919EAB">
                  Supported formats: JPG, or PNG. Maximum file size: 500KB
                  <br />
                  Recommended min. size: 284 × 160 px
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles)(GeneralSettings)
